import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatCheckbox, MatCheckboxChange, MatDialog, Sort } from '@angular/material';
import { NotificationInterface } from 'app/notifications/models';
import { NotificationService } from 'app/notifications/services/notification.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { NotificationDialogComponent } from '../notification-dialog/notification-dialog.component';

@Component({
  selector: 'cz-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
})

export class NotificationsComponent implements OnInit, OnDestroy {

  @Input() public notificationsForUser$: BehaviorSubject<Readonly<NotificationInterface[]>>;
  @Input() public showActions: boolean;

  public sortedData: NotificationInterface[];
  public bulkAction: string;
  public actionForm: FormGroup;

  public notificationCount: number = 10;
  public readonly showMoreIncrement: number = 10;

  @ViewChild('selectAllCheckbox') public selectAllCheckbox: MatCheckbox;

  private readonly destroy$: Subject<boolean> = new Subject<boolean>();
  private currentSort: Sort = {
    direction: 'desc',
    active: 'notificationDate',
  };

  constructor(
    private _formBuilder: FormBuilder,
    private readonly _notificationService: NotificationService,
    public dialog: MatDialog,
    public changeRef: ChangeDetectorRef) { }

  public ngOnInit(): void {
    if(this.notificationsForUser$) {
      this.notificationsForUser$.pipe(
      takeUntil(this.destroy$))
      .subscribe((notifications: NotificationInterface[]) => {
        this.sortedData = this.sortData(notifications, this.currentSort);
        this.actionForm = this._formBuilder.group(this.createActionFormControls(this.sortedData));
        this.changeRef.detectChanges();
      });
    }
    if(this.showActions && this.notificationsForUser$.value.length < 1) {
      this.showActions = false;
    }

  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public get allShown(): boolean {
    const total: number = this.notificationsForUser$.value.length;
    return total <= this.notificationCount;
  }

  public loadMoreNotifications(): void {
    this.notificationCount += this.showMoreIncrement;
  }

  public checkUncheckAll(event: MatCheckboxChange): void {
    this.sortedData.forEach((notification: NotificationInterface) => {
      this.actionForm.get(notification.id.toString()).setValue(event.checked);
    });
  }

  public changeSort(sort: Sort): void {
    if (!sort) {
      return;
    }

    this.currentSort = sort;

    this.sortedData = this.sortData(this.sortedData, sort);
  }

  public openNotificationDialog(notificationId: number): void {
    const notificationToDisplay: NotificationInterface = this.sortedData.find((notificationToFind: NotificationInterface) => {
      return notificationToFind.id === notificationId;
    });

    this._notificationService.updateManyReadUnread([notificationToDisplay], true);

    const dialogData: any = {
      notification: notificationToDisplay,
    };
    this.dialog.open(NotificationDialogComponent, {
      height: '600px',
      width: '800px',
      data: dialogData,
    });
  }

  public changeCheckedReadState(readState: boolean): void {
    // get all the checkboxes
    const keys: string[] = Object.keys(this.actionForm.value);
    // figure out which ones are checked and get their values
    const keyArray: string[] = keys.filter((key: string) => {
      return this.actionForm.value[key];
    });

    // only worry about anything if anything is selected
    if(keyArray.length > 0) {
      // convert the string values into numbers
      const notificationIdArray: number[] = keyArray.map((id: string) => Number(id));
      // transform current notification data into a map
      const notificationMap: {[id:number]:NotificationInterface} =
        this.sortedData.reduce(
          (notifications: {[id:number]:NotificationInterface}, notification: NotificationInterface) => {
            return {...notifications, ...{
              [notification.id]: notification,
            }};
          },
          {});
      // get the notifications we need to update
      const updateNotifications: NotificationInterface[] = [];
      notificationIdArray.forEach((id: number) => {
        updateNotifications.push(notificationMap[id]);
      });
      this.selectAllCheckbox.checked = false;
      this._notificationService.updateManyReadUnread(updateNotifications, readState);
    }
  }

  private createActionFormControls(notificationData: NotificationInterface[]): {} {
    const group: { [id: number]: FormControl } = {};
    if (!notificationData) { return group; }

    notificationData.forEach((notification: NotificationInterface) => {
      group[notification.id] = new FormControl(false);
    });

    return group;
  }

  private sortData(data: NotificationInterface[], sort: Sort): NotificationInterface[] {
    this.currentSort = sort;
    return data.sort((a: NotificationInterface, b: NotificationInterface) => {
      const isAsc: boolean = sort.direction === 'asc';
      return this.compare(a[sort.active], b[sort.active], isAsc);
    });
  }

  private compare(a: number | string | Date, b: number | string | Date, isAsc: boolean): number {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
}
